/**
 * Syncs variant-picker selection to the product media gallery by selecting
 * the matching slideshow slide — without rewriting the gallery HTML.
 *
 * When the picker change originates from the gallery (`data-fcd-sync-from-gallery`),
 * the swatch updates but the gallery slide is left alone.
 */
import { ThemeEvents } from '@theme/events';

const SYNC_FROM_GALLERY_ATTR = 'fcdSyncFromGallery';

/**
 * @param {Element | null | undefined} el
 * @returns {boolean}
 */
function isCardContext(el) {
  return Boolean(el?.closest('product-card') || el?.closest('quick-add-dialog'));
}

/**
 * @param {HTMLElement} picker
 * @returns {boolean}
 */
function isSyncFromGallery(picker) {
  return picker.dataset[SYNC_FROM_GALLERY_ATTR] === 'true';
}

/**
 * @param {HTMLElement} picker
 */
function clearSyncFromGallery(picker) {
  delete picker.dataset[SYNC_FROM_GALLERY_ATTR];
}

/**
 * @param {Element} el
 * @returns {ParentNode}
 */
function getScope(el) {
  return el.closest('.shopify-section, featured-product-information, dialog') || document;
}

/**
 * @param {EventTarget | null} target
 * @returns {HTMLElement | null}
 */
function getVariantPicker(target) {
  if (!(target instanceof HTMLElement)) return null;
  if (target.matches('variant-picker')) return target;
  return target.closest('variant-picker');
}

/**
 * @param {HTMLElement} picker
 * @param {string | undefined} optionValueId
 * @returns {string | null}
 */
function getOptionMediaId(picker, optionValueId) {
  if (optionValueId) {
    const byId = /** @type {HTMLElement | null} */ (
      picker.querySelector(`[data-option-value-id="${CSS.escape(String(optionValueId))}"]`)
    );
    if (byId?.dataset?.optionMediaId) return byId.dataset.optionMediaId;
  }

  const withMedia = /** @type {HTMLElement | null} */ (
    picker.querySelector(
      'fieldset input:checked[data-option-media-id], select option[selected][data-option-media-id]'
    )
  );

  return withMedia?.dataset?.optionMediaId || null;
}

/**
 * @param {HTMLElement} picker
 * @param {string} mediaId
 */
function selectGallerySlide(picker, mediaId) {
  if (!mediaId || isCardContext(picker) || isSyncFromGallery(picker)) return;

  const scope = getScope(picker);
  const gallery = /** @type {(HTMLElement & { refs?: { slideshow?: { select?: Function } } }) | null} */ (
    scope.querySelector('media-gallery')
  );
  if (!gallery) return;

  const slideshow = gallery.refs?.slideshow || gallery.querySelector('slideshow-component');
  if (!slideshow || typeof slideshow.select !== 'function') return;

  // Programmatic select (no event) → userInitiated:false, so gallery→swatch sync skips.
  slideshow.select({ id: String(mediaId) }, undefined, { animate: true });
}

/**
 * Immediate feedback from the clicked option's featured media.
 * @param {Event} event
 */
function onVariantSelected(event) {
  const picker = getVariantPicker(event.target);
  if (!picker || isCardContext(picker)) return;

  // Gallery drove this change: keep the current slide, only update the picker.
  if (isSyncFromGallery(picker)) return;

  const optionValueId = /** @type {{ detail?: { resource?: { id?: string } } }} */ (event).detail?.resource
    ?.id;
  const mediaId = getOptionMediaId(picker, optionValueId);
  if (!mediaId) return;

  selectGallerySlide(picker, mediaId);
}

/**
 * After the variant resolves, correct the slide using featured_media
 * (covers options without data-option-media-id, e.g. size).
 * @param {Event} event
 */
function onVariantUpdate(event) {
  const detail = /** @type {{ detail?: { data?: { newProduct?: unknown }, resource?: { featured_media?: { id?: string | number } } } }} */ (
    event
  ).detail;

  // Combined listing product switch: section morph / gallery replace still applies.
  if (detail?.data?.newProduct) return;

  const picker = getVariantPicker(event.target);
  if (!picker || isCardContext(picker)) return;

  // Gallery→picker sync: clear the flag and do not move the slideshow.
  if (isSyncFromGallery(picker)) {
    clearSyncFromGallery(picker);
    return;
  }

  const mediaId = detail?.resource?.featured_media?.id;
  if (mediaId == null) return;

  selectGallerySlide(picker, String(mediaId));
}

document.addEventListener(ThemeEvents.variantSelected, onVariantSelected);
document.addEventListener(ThemeEvents.variantUpdate, onVariantUpdate);
